import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { 
  Leaf, 
  Heart, 
  Sparkles, 
  MapPin, 
  Phone, 
  Mail,
  ArrowRight,
  Calendar,
  Star,
  Gift,
  Download,
  Smartphone
} from 'lucide-react';
import ServiceCard from '../components/ServiceCard';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_hilanta-wellness/artifacts/yzd7rzdb_76b87d66-fa32-4fab-93bc-4ef8dc416573.jpg";
const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_bc32ed69-7376-427e-9219-63e5d9991347/artifacts/oatathq9_8c1d18c6-c01b-430d-b4fe-8cdef5adf395.jpg";
const MASSAGE_IMAGE = "https://images.pexels.com/photos/28321583/pexels-photo-28321583.jpeg?w=800";
const NATURE_IMAGE = "https://images.pexels.com/photos/6694168/pexels-photo-6694168.jpeg?w=800";
const WORKSHOP_IMAGE = "https://images.pexels.com/photos/30225356/pexels-photo-30225356.jpeg?w=800";
const SPA_IMAGE = "https://images.pexels.com/photos/9268045/pexels-photo-9268045.jpeg?auto=compress&cs=tinysrgb&w=800";

const Home = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        // First try to seed the database
        await axios.post(`${API}/seed`);
        // Then fetch services
        const response = await axios.get(`${API}/services`);
        setServices(response.data);
      } catch (error) {
        console.error('Error fetching services:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchServices();
  }, []);

  const featuredServices = services.slice(0, 3);

  return (
    <div className="min-h-screen" data-testid="home-page">
      <Helmet>
        <title>Hilanta | Massages & Bien-être - Santé du corps, paix d'esprit</title>
        <meta name="description" content="Hilanta, votre espace de massages et bien-être. Réservez en ligne votre séance de massage ou soin. Santé du corps, paix d'esprit et amour du cœur." />
      </Helmet>
      
      {/* Hero Section */}
      <section 
        className="min-h-[90vh] flex items-center bg-cover bg-center bg-no-repeat relative" 
        style={{ 
          backgroundImage: `url(${HERO_IMAGE})` 
        }}
        data-testid="hero-section"
      >
        {/* Overlay pour lisibilité du texte - Plus opaque */}
        <div className="absolute inset-0 bg-gradient-to-b from-white/80 via-white/70 to-white/90"></div>
        
        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <div className="max-w-2xl mx-auto">
            <img 
              src={LOGO_URL} 
              alt="Hilanta Logo" 
              className="w-28 h-28 md:w-36 md:h-36 mx-auto mb-6 rounded-full shadow-xl border-4 border-gold/50 bg-white object-cover"
              data-testid="hero-logo"
            />
            
            <h1 className="font-heading text-5xl md:text-7xl font-light text-warm-brown mb-4 drop-shadow-sm">
              HILANTA
            </h1>
            
            <p className="font-body text-lg md:text-xl text-warm-brown mb-2 font-medium">
              Massages soins bien-être, Naturopathie, Activités créatives
            </p>
            
            <p className="font-heading text-2xl md:text-3xl text-terracotta italic mb-8 drop-shadow-sm">
              Santé du corps, paix d'esprit et amour du cœur
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link to="/reservation">
                <Button 
                  className="bg-terracotta hover:bg-terracotta-600 text-white rounded-full px-8 py-6 text-lg font-body shadow-lg transition-transform hover:scale-105"
                  data-testid="hero-cta-button"
                >
                  <Calendar className="mr-2" size={20} />
                  Prendre rendez-vous
                </Button>
              </Link>
              <Link to="/services">
                <Button 
                  variant="outline"
                  className="border-2 border-terracotta text-terracotta hover:bg-terracotta hover:text-white rounded-full px-8 py-6 text-lg font-body"
                  data-testid="hero-services-button"
                >
                  Découvrir nos soins
                  <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-20 md:py-32 bg-sand" data-testid="services-overview">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="font-body text-sm uppercase tracking-widest text-sage-700">Nos Services</span>
            <h2 className="font-heading text-4xl md:text-5xl text-warm-brown mt-2">
              Un univers de bien-être
            </h2>
          </div>

          {/* Service Categories Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {/* Massages */}
            <div className="group relative overflow-hidden rounded-3xl h-80">
              <img 
                src={MASSAGE_IMAGE} 
                alt="Massages" 
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-warm-brown/80 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                <Leaf className="mb-2 text-gold" size={28} />
                <h3 className="font-heading text-2xl mb-2">Les Massages</h3>
                <p className="font-body text-sm text-white/80">
                  Massage signature, crânien, réflexologie, Chi Nei Tsang...
                </p>
              </div>
            </div>

            {/* Naturopathie */}
            <div className="group relative overflow-hidden rounded-3xl h-80">
              <img 
                src={NATURE_IMAGE} 
                alt="Naturopathie" 
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-warm-brown/80 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                <Heart className="mb-2 text-gold" size={28} />
                <h3 className="font-heading text-2xl mb-2">Naturopathie</h3>
                <p className="font-body text-sm text-white/80">
                  Conseils personnalisés pour retrouver vitalité et équilibre
                </p>
              </div>
            </div>

            {/* Activités */}
            <div className="group relative overflow-hidden rounded-3xl h-80">
              <img 
                src={WORKSHOP_IMAGE} 
                alt="Activités créatives" 
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-warm-brown/80 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                <Sparkles className="mb-2 text-gold" size={28} />
                <h3 className="font-heading text-2xl mb-2">Activités Créatives</h3>
                <p className="font-body text-sm text-white/80">
                  Ateliers enfants et conférences santé naturelle
                </p>
              </div>
            </div>
          </div>

          <div className="text-center">
            <Link to="/services">
              <Button 
                variant="outline"
                className="border-2 border-sage text-sage-700 hover:bg-sage hover:text-white rounded-full px-8 py-4 font-body"
                data-testid="view-all-services"
              >
                Voir tous nos services
                <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Services */}
      {!loading && featuredServices.length > 0 && (
        <section className="py-20 md:py-32" data-testid="featured-services">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <span className="font-body text-sm uppercase tracking-widest text-terracotta">Populaires</span>
              <h2 className="font-heading text-4xl md:text-5xl text-warm-brown mt-2">
                Nos soins phares
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {featuredServices.map((service) => (
                <ServiceCard key={service.id} service={service} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* About Section */}
      <section className="py-20 md:py-32 bg-terracotta-50" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="font-body text-sm uppercase tracking-widest text-terracotta">À propos de nous</span>
              <h2 className="font-heading text-4xl md:text-5xl text-warm-brown mt-2 mb-6">
                L'histoire d'Hilanta
              </h2>
              <div className="space-y-4 font-body text-warm-brown-light leading-relaxed">
                <p>
                  <strong className="text-warm-brown">HILANTA</strong>, qui signifie arbre en Araméen, est née de la passion et de l'union de trois sœurs animées par l'amour du bien-être, des soins naturels et de la créativité.
                </p>
                <p>
                  Solidement enracinée comme un arbre, notre entreprise grandit avec des valeurs fortes : <span className="text-terracotta font-medium">authenticité, bienveillance, respect de la nature et équilibre</span>.
                </p>
                <p>
                  Nous croyons au pouvoir des soins naturels pour nourrir le corps et apaiser l'esprit. À travers nos activités créatives, nos conférences, nos massages et nos accompagnements en naturopathie, nous souhaitons offrir à tous, petits et grands, des moments de douceur, de partage et d'harmonie.
                </p>
                <p className="italic text-sage-700">
                  Chez HILANTA, chaque détail est pensé avec cœur. Notre mission est de vous accompagner vers un mieux-être durable, dans un cadre chaleureux et inspirant.
                </p>
              </div>
            </div>
            <div className="flex justify-center">
              <div className="relative">
                <div className="absolute -inset-4 bg-gold/20 rounded-full transform rotate-3"></div>
                <img 
                  src={LOGO_URL} 
                  alt="Hilanta Logo" 
                  className="relative w-48 h-48 sm:w-64 sm:h-64 md:w-80 md:h-80 object-cover rounded-full shadow-xl border-4 border-white bg-white"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mobile App Section */}
      <section className="py-16 bg-gradient-to-r from-terracotta to-terracotta-600" data-testid="app-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="text-center md:text-left">
              <div className="inline-flex items-center gap-2 bg-white/20 rounded-full px-4 py-2 mb-4">
                <Smartphone className="text-white" size={20} />
                <span className="text-white/90 text-sm font-medium">Application mobile</span>
              </div>
              <h2 className="font-heading text-3xl md:text-4xl text-white mb-4">
                Téléchargez l'app Hilanta
              </h2>
              <p className="text-white/80 font-body max-w-md">
                Accédez à vos réservations, découvrez nos soins et prenez rendez-vous en un clic depuis votre téléphone.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={() => {
                  const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
                  if (isIOS) {
                    alert(
                      "Pour installer Hilanta sur votre iPhone :\n\n" +
                      "1. Appuyez sur le bouton Partager (carré avec flèche)\n" +
                      "2. Faites défiler et appuyez sur \"Sur l'écran d'accueil\"\n" +
                      "3. Appuyez sur \"Ajouter\""
                    );
                  } else {
                    // Try to trigger install prompt
                    if (window.deferredPrompt) {
                      window.deferredPrompt.prompt();
                    } else {
                      alert(
                        "Pour installer Hilanta :\n\n" +
                        "1. Ouvrez ce site dans Chrome ou Safari\n" +
                        "2. Cliquez sur les 3 points (⋮) en haut à droite\n" +
                        "3. Sélectionnez \"Installer l'application\""
                      );
                    }
                  }
                }}
                className="flex items-center gap-3 bg-white text-terracotta px-6 py-4 rounded-2xl font-semibold hover:bg-gray-100 transition-colors shadow-lg"
                data-testid="install-app-btn"
              >
                <Download size={24} />
                <div className="text-left">
                  <div className="text-xs text-terracotta/70">Télécharger</div>
                  <div className="text-lg">Application Hilanta</div>
                </div>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Location Section */}
      <section className="py-20 md:py-32" data-testid="location-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="font-body text-sm uppercase tracking-widest text-sage-700">Nous trouver</span>
            <h2 className="font-heading text-4xl md:text-5xl text-warm-brown mt-2">
              Venez nous rendre visite
            </h2>
          </div>

          <div className="bg-white rounded-3xl shadow-lg overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-2">
              {/* Map placeholder */}
              <div className="h-80 lg:h-auto bg-sand">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2903.5!2d5.4!3d43.3!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDPCsDE4JzAwLjAiTiA1wrAyNCcwMC4wIkU!5e0!3m2!1sfr!2sfr!4v1600000000000!5m2!1sfr!2sfr"
                  width="100%"
                  height="100%"
                  style={{ border: 0, minHeight: '320px' }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Hilanta Location"
                ></iframe>
              </div>

              {/* Contact Info */}
              <div className="p-8 lg:p-12">
                <h3 className="font-heading text-3xl text-warm-brown mb-6">Informations</h3>
                
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-terracotta-100 rounded-full">
                      <MapPin className="text-terracotta" size={24} />
                    </div>
                    <div>
                      <h4 className="font-body font-semibold text-warm-brown">Adresse</h4>
                      <a 
                        href="https://www.google.com/maps/search/?api=1&query=58+Avenue+Maréchal+Foch+13004+Marseille"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-body text-warm-brown-light hover:text-terracotta transition-colors"
                        data-testid="address-link"
                      >
                        58 Avenue Maréchal Foch<br />
                        13004 Marseille
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-sage-100 rounded-full">
                      <Phone className="text-sage-700" size={24} />
                    </div>
                    <div>
                      <h4 className="font-body font-semibold text-warm-brown">Téléphone</h4>
                      <a href="tel:0960421822" className="font-body text-warm-brown-light hover:text-terracotta" data-testid="contact-phone">
                        09 60 42 18 22
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-gold/20 rounded-full">
                      <Mail className="text-gold" size={24} />
                    </div>
                    <div>
                      <h4 className="font-body font-semibold text-warm-brown">Email</h4>
                      <a href="mailto:la.hilanta@gmail.com" className="font-body text-warm-brown-light hover:text-terracotta" data-testid="contact-email">
                        la.hilanta@gmail.com
                      </a>
                    </div>
                  </div>

                  <div className="pt-4 border-t border-sand">
                    <h4 className="font-body font-semibold text-warm-brown mb-2">Correspondances</h4>
                    <ul className="font-body text-sm text-warm-brown-light space-y-1">
                      <li>Tramway 2 : Foch Boisson</li>
                      <li>Métro 1 : La Blancarde</li>
                      <li>Gare : Marseille Blancarde</li>
                    </ul>
                  </div>
                </div>

                <Link to="/contact" className="mt-8 block">
                  <Button 
                    className="w-full bg-terracotta hover:bg-terracotta-600 text-white rounded-full py-4 font-body"
                    data-testid="contact-cta"
                  >
                    Nous contacter
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
